import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import apiService from '../services/api.service';

const fmtDate = (value: any): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

const money = (currency: string, value: any) => `${currency || 'SAR'} ${Number(value || 0).toFixed(2)}`;

const POBulkApproval: React.FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [submittingAction, setSubmittingAction] = React.useState<'approve' | 'reject' | 'rework' | null>(null);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [data, setData] = React.useState<any | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [result, setResult] = React.useState<any | null>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const load = React.useCallback(async () => {
    if (!token) {
      setError('Invalid bulk approval link');
      setLoading(false);
      return;
    }
    try {
      setError('');
      const res = await apiService.resolvePOBulkApprovalToken(token);
      const d = (res as any)?.data || res || {};
      const payload = d.data || d;
      setData(payload);
      setSelectedIds([]);
      setResult(null);
      setPage(0);
    } catch (e: any) {
      setError(String(e?.response?.data?.message || 'Unable to open bulk approval link'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const items = React.useMemo(
    () => (Array.isArray(data?.items) ? data.items : []),
    [data?.items]
  );
  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < items.length;
  const pagedItems = React.useMemo(
    () => items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [items, page, rowsPerPage]
  );

  React.useEffect(() => {
    const maxPage = Math.max(Math.ceil(items.length / rowsPerPage) - 1, 0);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [items.length, page, rowsPerPage]);

  const toggleId = (poId: number) => {
    setSelectedIds((prev) => (
      prev.includes(poId) ? prev.filter((id) => id !== poId) : [...prev, poId]
    ));
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(items.map((item: any) => Number(item.poid)));
  };

  const handleProcess = async (action: 'approve' | 'reject' | 'rework') => {
    if (!token) return;
    if (selectedIds.length === 0) {
      setError('Select at least one PO');
      return;
    }
    if ((action === 'reject' || action === 'rework') && !String(comment || '').trim()) {
      setError(`Comment is required for ${action}`);
      return;
    }

    setSubmitting(true);
    setSubmittingAction(action);
    setError('');
    setSuccess('');
    setResult(null);
    try {
      const res = await apiService.processPOBulkApprovalToken(token, {
        poIds: selectedIds,
        action,
        comments: comment
      });
      const d = (res as any)?.data || res || {};
      const payload = d.data || d;
      setSuccess(String(d.message || 'Bulk action completed successfully'));
      setResult(payload);
      await load();
    } catch (e: any) {
      setError(String(e?.response?.data?.message || 'Failed to process selected POs'));
    } finally {
      setSubmitting(false);
      setSubmittingAction(null);
    }
  };

  return (
    <Box
      component="main"
      id="main-content"
      sx={{
        minHeight: '100vh',
        '@supports (min-height: 100svh)': { minHeight: '100svh' },
        bgcolor: '#f8fafc',
        py: { xs: 2, md: 4 },
        px: { xs: 1.5, md: 2 },
      }}
    >
      <Paper sx={{ width: '100%', maxWidth: 1180, mx: 'auto', p: { xs: 2, md: 3 } }} aria-labelledby="po-bulk-heading">
        {loading ? (
          <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: 240 }} role="status" aria-live="polite">
            <CircularProgress aria-label="Loading pending POs" />
            <Typography variant="body2">Loading pending POs...</Typography>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography
                  component="h1"
                  id="po-bulk-heading"
                  variant="h4"
                  sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: 22, sm: 26, md: 30 } }}
                >
                  PO Bulk Approval
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review all currently pending POs for this approval link and process them together.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="outlined" onClick={() => void load()} disabled={submitting}>
                  Refresh
                </Button>
              </Stack>
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? <Alert severity="success">{success}</Alert> : null}

            {data ? (
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                  <Box>
                    <Typography variant="body2"><strong>Approver:</strong> {data.approverName || '-'}</Typography>
                    <Typography variant="body2"><strong>Pending PO Count:</strong> {Number(data.pendingCount || 0)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2"><strong>Link Valid Until:</strong> {fmtDate(data.expiryDate)}</Typography>
                    <Typography variant="body2"><strong>Selected:</strong> {selectedIds.length}</Typography>
                  </Box>
                </Stack>
              </Box>
            ) : null}

            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleAll}
                          disabled={submitting}
                        />
                      </TableCell>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Requested By</TableCell>
                      <TableCell>PO Total</TableCell>
                      <TableCell>Estimated Cost</TableCell>
                      <TableCell>Profit Margin</TableCell>
                      <TableCell>Margin %</TableCell>
                      <TableCell>PO Date</TableCell>
                      <TableCell>Required Date</TableCell>
                      <TableCell>Step</TableCell>
                      <TableCell>Open</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} align="center">
                          No pending POs are available for this link.
                        </TableCell>
                      </TableRow>
                    ) : pagedItems.map((item: any) => {
                      const poId = Number(item.poid);
                      const selected = selectedIds.includes(poId);
                      return (
                        <TableRow key={poId} hover selected={selected}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={selected} onChange={() => toggleId(poId)} disabled={submitting} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{item.poNumber || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {item.allocationCode || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.projectName || item.allocationType || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{item.vendorCode ? `${item.vendorCode} - ${item.vendorName || ''}` : (item.vendorName || '-')}</TableCell>
                          <TableCell>{item.requestedByName || '-'}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{money(item.currency, item.totalAmount)}</TableCell>
                          <TableCell>{money(item.currency, item.estimatedCost)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: Number(item.profitAmount || 0) >= 0 ? '#166534' : '#b91c1c' }}>
                            {money(item.currency, item.profitAmount)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: Number(item.marginPct || 0) >= 0 ? '#166534' : '#b91c1c' }}>
                            {Number(item.marginPct || 0).toFixed(2)}%
                          </TableCell>
                          <TableCell>{fmtDate(item.poDate)}</TableCell>
                          <TableCell>{fmtDate(item.requiredDate)}</TableCell>
                          <TableCell>{Number(item.stepNumber || 0)}</TableCell>
                          <TableCell>
                            <Button variant="outlined" size="small" onClick={() => navigate(`/po/${poId}`)} disabled={submitting}>
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              <TablePagination
                component="div"
                count={items.length}
                page={page}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value) || 10);
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Paper>

            <TextField
              label="Comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              disabled={submitting}
              helperText="This comment is written into PO approval history and will be visible to other approvers."
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <Button
                variant="contained"
                color="success"
                onClick={() => void handleProcess('approve')}
                disabled={submitting || items.length === 0}
              >
                {submittingAction === 'approve' ? 'Processing...' : 'Approve Selected'}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => void handleProcess('reject')}
                disabled={submitting || items.length === 0}
              >
                {submittingAction === 'reject' ? 'Processing...' : 'Reject Selected'}
              </Button>
              <Button
                variant="contained"
                sx={{ bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}
                onClick={() => void handleProcess('rework')}
                disabled={submitting || items.length === 0}
              >
                {submittingAction === 'rework' ? 'Processing...' : 'Rework Selected'}
              </Button>
            </Stack>

            {result ? (
              <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Result
                </Typography>
                <Typography variant="body2"><strong>Success:</strong> {Number(result.successCount || 0)}</Typography>
                <Typography variant="body2"><strong>Failed:</strong> {Number(result.failedCount || 0)}</Typography>
                {Array.isArray(result.failed) && result.failed.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    {result.failed.map((row: any) => (
                      <Typography key={`${row.poid}-${row.message}`} variant="body2" color="error.main">
                        PO {row.poid}: {row.message}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
              </Box>
            ) : null}

            <Typography variant="caption" color="text.secondary">
              Opening this link does not change approval status. Only the selected submit action updates the workflow.
            </Typography>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default POBulkApproval;
